import React from "react";
import { Link } from "react-router-dom";
import {
  UserPlus, Car, Wallet, Wrench, FileText, Plane, Trophy,
  Search, Calendar, ClipboardList, Trash2, Ban, Lightbulb, CheckCircle2, XCircle,
} from "lucide-react";

const STEPS = [
  {
    n: 1,
    title: "Réception du client",
    icon: UserPlus,
    nav: "/clients",
    navLabel: "Clients",
    body: [
      "Cliquer + Nouveau client.",
      "Saisir au minimum : Nom et Téléphone.",
      "Optionnel : email, adresse (utile pour facture et carte grise).",
    ],
    tip: "Si le client existe déjà, utilisez la Recherche (sidebar) pour éviter les doublons.",
  },
  {
    n: 2,
    title: "Créer la vente",
    icon: Car,
    nav: "/ventes",
    navLabel: "Ventes en cours",
    body: [
      "Cliquer + Nouvelle vente.",
      "Renseigner : Client, Plaque, Marque/Modèle (autocomplétion), Prix de vente, Date de départ prévue.",
      "Optionnel : N° VO, année, kilométrage, petite note.",
    ],
    tip: "Astuce : utilisez le Scan IA sur la carte grise pour pré-remplir automatiquement marque, modèle, plaque et année.",
  },
  {
    n: 3,
    title: "Enregistrer l'acompte",
    icon: Wallet,
    nav: "/paiements",
    navLabel: "Paiements",
    body: [
      "Cliquer + Nouveau paiement.",
      "Lier à la vente (recherche live par plaque ou client).",
      "Saisir le montant et la méthode (Espèces, CB, Virement, Chèque).",
      "La vente passe automatiquement en statut PARTIEL.",
    ],
    tip: "Le reçu PDF est généré automatiquement (TVA 262 / prix HT mentionné).",
  },
  {
    n: 4,
    title: "Préparer le véhicule",
    icon: Wrench,
    nav: "/atelier",
    navLabel: "Atelier",
    body: [
      "Suivre la liste des interventions à réaliser.",
      "Ajouter des coûts d'intervention si besoin.",
      "Cocher \"Prêt\" dès que le véhicule est livrable.",
    ],
    tip: "Le Tableau de bord affiche les alertes \"À préparer\" pour les véhicules avec date de départ approchant.",
  },
  {
    n: 5,
    title: "Solde paiement + Reçu final",
    icon: FileText,
    nav: "/paiements",
    navLabel: "Paiements",
    body: [
      "Encaisser le solde restant (colonne \"Restant\").",
      "La vente passe automatiquement à PAYÉ.",
      "Générer le reçu PDF final pour le client.",
    ],
    tip: "Vérifier que le total payé = prix de vente avant de valider le départ.",
  },
  {
    n: 6,
    title: "Marquer le départ + Carte grise",
    icon: Plane,
    nav: "/ventes",
    navLabel: "Ventes en cours",
    body: [
      "Ouvrir la vente.",
      "Renseigner la date de départ effective.",
      "Marquer Carte grise : OK dès qu'elle est reçue/transmise.",
    ],
    tip: "Si la carte grise n'est pas reçue à temps, une alerte rouge apparaîtra sur le Tableau de bord.",
  },
  {
    n: 7,
    title: "Clôturer la vente",
    icon: Trophy,
    nav: "/vo-vendus",
    navLabel: "VO vendus",
    body: [
      "Le véhicule apparaît automatiquement dans VO vendus dès que la vente est payée et le départ effectué.",
      "Aucune action manuelle nécessaire.",
    ],
    tip: "Cet onglet sert au reporting comptable et au suivi des ventes finalisées.",
  },
];

const SHORTCUTS = [
  { icon: Search, label: "Trouver une voiture", target: "Recherche (sidebar) ou barre en haut du Dashboard" },
  { icon: Calendar, label: "Voir l'agenda des départs", target: "Calendrier" },
  { icon: ClipboardList, label: "Suivre une demande client en attente", target: "Commandes Client" },
  { icon: Trash2, label: "Retrouver une vente supprimée < 7 jours", target: "Corbeille" },
  { icon: Ban, label: "Voir une vente annulée", target: "Ventes annulées" },
];

const DO = [
  "Toujours lier l'acompte à une vente (jamais paiement orphelin).",
  "Toujours vérifier la plaque saisie (la recherche en dépend).",
  "Utiliser la petite note pour les détails verbaux du client.",
  "Consulter le Dashboard chaque matin pour repérer les retards & départs imminents.",
];

const DONT = [
  "Modifier une vente après paiement complet sans raison.",
  "Supprimer un client lié à des ventes en cours.",
];

export default function AideCommercial() {
  return (
    <div className="space-y-10 max-w-5xl">
      <header>
        <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
          Guide commercial · v1.0
        </div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Procédure type</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          7 étapes pour gérer une vente de A à Z. Chaque étape est liée à un onglet de l'application.
        </p>

        {/* Visual flow */}
        <div className="mt-6 flex flex-wrap items-center gap-1 text-xs text-muted-foreground" data-testid="commercial-flow">
          <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-sm">Client arrive</span>
          <span>→</span>
          <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-sm">Vente créée</span>
          <span>→</span>
          <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-sm">Acompte</span>
          <span>→</span>
          <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-sm">Préparation</span>
          <span>→</span>
          <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-sm">Solde + Reçu</span>
          <span>→</span>
          <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-sm">Départ</span>
          <span>→</span>
          <span className="px-2.5 py-1 bg-klein/10 text-klein font-medium rounded-sm">VO vendus</span>
        </div>
      </header>

      {/* Steps */}
      <section className="space-y-4">
        {STEPS.map((s) => {
          const Icon = s.icon;
          return (
            <article
              key={s.n}
              data-testid={`commercial-step-${s.n}`}
              className="bg-card border border-border rounded-sm p-6 flex gap-5"
            >
              <div className="flex-shrink-0 flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-klein text-white flex items-center justify-center font-black text-lg">
                  {s.n}
                </div>
                <Icon size={20} className="mt-3 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <h2 className="font-display font-bold text-xl">{s.title}</h2>
                  <Link
                    to={s.nav}
                    className="text-xs text-klein hover:underline"
                    data-testid={`commercial-step-${s.n}-link`}
                  >
                    Aller à {s.navLabel} →
                  </Link>
                </div>
                <ul className="mt-3 space-y-1.5 text-sm text-foreground/90">
                  {s.body.map((b, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-klein font-bold">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {s.tip && (
                  <div className="mt-3 flex gap-2 items-start bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-sm px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                    <Lightbulb size={14} className="flex-shrink-0 mt-0.5" />
                    <span>{s.tip}</span>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </section>

      {/* Shortcuts */}
      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-4">Raccourcis utiles</h2>
        <div className="bg-card border border-border rounded-sm divide-y divide-border">
          {SHORTCUTS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="flex items-center gap-4 px-5 py-3 text-sm">
                <Icon size={16} className="text-muted-foreground flex-shrink-0" />
                <span className="font-medium flex-1">{s.label}</span>
                <span className="text-muted-foreground text-xs">{s.target}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Do / Don't */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded-sm p-5">
          <div className="flex items-center gap-2 font-semibold text-emerald-900 dark:text-emerald-200 mb-3">
            <CheckCircle2 size={18} /> À FAIRE
          </div>
          <ul className="space-y-2 text-sm text-emerald-950 dark:text-emerald-100">
            {DO.map((d, i) => <li key={i}>✅ {d}</li>)}
          </ul>
        </div>
        <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 rounded-sm p-5">
          <div className="flex items-center gap-2 font-semibold text-rose-900 dark:text-rose-200 mb-3">
            <XCircle size={18} /> À ÉVITER
          </div>
          <ul className="space-y-2 text-sm text-rose-950 dark:text-rose-100">
            {DONT.map((d, i) => <li key={i}>❌ {d}</li>)}
          </ul>
        </div>
      </section>

      <footer className="text-xs text-muted-foreground pt-4 border-t border-border">
        Pour toute question fonctionnelle, contactez votre administrateur · Auto-Compta v1.0
      </footer>
    </div>
  );
}
